import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useReducedMotion } from 'framer-motion';
interface WireSculptureProps {
  active: number;
}
export function WireSculpture({
  active
}: WireSculptureProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(active);
  const reduce = useReducedMotion();
  useEffect(() => {
    activeRef.current = active;
  }, [active]);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0.4, 6.4);
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const key = new THREE.DirectionalLight(0xffb08a, 2.2);
    key.position.set(3, 4, 5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xea4e1b, 1.6);
    rim.position.set(-4, -1, -3);
    scene.add(rim);
    const grid = new THREE.GridHelper(12, 24, 0x4a2a1e, 0x2a1d18);
    grid.position.y = -2.1;
    scene.add(grid);
    const group = new THREE.Group();
    scene.add(group);
    const geometries: THREE.BufferGeometry[] = [new THREE.IcosahedronGeometry(1.55, 1), new THREE.TorusKnotGeometry(1.0, 0.32, 110, 12), new THREE.BoxGeometry(2.1, 2.1, 2.1, 3, 3, 3), new THREE.SphereGeometry(1.55, 20, 14), new THREE.DodecahedronGeometry(1.6, 0)];
    const disposables: {
      dispose: () => void;
    }[] = [];
    const objects = geometries.map((g, i) => {
      const obj = new THREE.Group();
      const wireGeo = new THREE.WireframeGeometry(g);
      const wireMat = new THREE.LineBasicMaterial({
        color: 0xea4e1b,
        transparent: true,
        opacity: 0.9
      });
      const pointMat = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 0.04,
        transparent: true,
        opacity: 0.85
      });
      const solidMat = new THREE.MeshStandardMaterial({
        color: i === 4 ? 0xd9d2cc : 0x1c1513,
        roughness: 0.55,
        metalness: 0.15,
        flatShading: true,
        transparent: true,
        opacity: i === 4 ? 0.95 : 0.55,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1
      });
      obj.add(new THREE.Mesh(g, solidMat));
      obj.add(new THREE.LineSegments(wireGeo, wireMat));
      obj.add(new THREE.Points(g, pointMat));
      obj.scale.setScalar(0.0001);
      group.add(obj);
      disposables.push(g, wireGeo, wireMat, pointMat, solidMat);
      return {
        obj,
        scale: 0
      };
    });
    const resize = () => {
      const {
        clientWidth: w,
        clientHeight: h
      } = mount;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(mount);
    const pointer = {
      x: 0,
      y: 0
    };
    const onMove = (e: PointerEvent) => {
      const r = mount.getBoundingClientRect();
      pointer.x = (e.clientX - r.left) / r.width - 0.5;
      pointer.y = (e.clientY - r.top) / r.height - 0.5;
    };
    const onLeave = () => {
      pointer.x = 0;
      pointer.y = 0;
    };
    mount.addEventListener('pointermove', onMove);
    mount.addEventListener('pointerleave', onLeave);
    let visible = true;
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(mount);
    let raf = 0;
    let spin = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!visible) return;
      objects.forEach((o, i) => {
        const target = i === activeRef.current ? 1 : 0;
        o.scale += (target - o.scale) * (reduce ? 1 : 0.1);
        o.obj.visible = o.scale > 0.01;
        o.obj.scale.setScalar(Math.max(o.scale, 0.0001));
      });
      if (!reduce) spin += 0.004;
      group.rotation.y += (spin + pointer.x * 1.2 - group.rotation.y) * 0.08;
      group.rotation.x += (pointer.y * 0.6 + 0.15 - group.rotation.x) * 0.08;
      renderer.render(scene, camera);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      mount.removeEventListener('pointermove', onMove);
      mount.removeEventListener('pointerleave', onLeave);
      disposables.forEach(d => d.dispose());
      grid.geometry.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [reduce]);
  return <div ref={mountRef} aria-hidden className="absolute inset-0 cursor-grab" data-id="element-251" data-mp="36nphlz3znfzi" />;
}